let loadPromise = null

/**
 * Loads the Google Maps JavaScript API with the Places library once.
 * @returns {Promise<void>}
 */
export function loadGoogleMapsPlaces() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in the browser'))
  }

  if (window.google?.maps?.places) {
    return Promise.resolve()
  }

  const key = import.meta.env.VITE_GOOGLE_API_KEY
  if (!key) {
    return Promise.reject(new Error('VITE_GOOGLE_API_KEY is not set'))
  }

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      let settled = false
      const settleOk = () => {
        if (settled) return
        if (window.google?.maps?.places) {
          settled = true
          resolve()
        }
      }
      const settleErr = (message) => {
        if (settled) return
        settled = true
        loadPromise = null
        reject(new Error(message))
      }

      const onLoad = () => settleOk()

      const existing = document.querySelector('script[data-google-maps-places]')
      if (existing) {
        if (window.google?.maps?.places) {
          settleOk()
          return
        }
        existing.addEventListener('load', onLoad)
        const t0 = Date.now()
        const iv = setInterval(() => {
          if (window.google?.maps?.places) {
            clearInterval(iv)
            existing.removeEventListener('load', onLoad)
            settleOk()
          } else if (Date.now() - t0 > 25000) {
            clearInterval(iv)
            existing.removeEventListener('load', onLoad)
            settleErr('Google Maps Places load timeout')
          }
        }, 50)
        return
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`
      script.async = true
      script.defer = true
      script.dataset.googleMapsPlaces = 'true'
      script.onerror = () => settleErr('Failed to load Google Maps script')
      script.addEventListener('load', onLoad)
      document.head.appendChild(script)

      const t0 = Date.now()
      const iv = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(iv)
          script.removeEventListener('load', onLoad)
          settleOk()
        } else if (Date.now() - t0 > 25000) {
          clearInterval(iv)
          script.removeEventListener('load', onLoad)
          settleErr('Google Maps Places load timeout')
        }
      }, 50)
    })
  }

  return loadPromise
}
